import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, MessageCircle, Video } from 'lucide-react';
import { useState } from 'react';

const footerLinks = {
  products: [
    { label: 'GH二代系列', path: '/products?category=gh2' },
    { label: '攻鲢手系列', path: '/products?category=gl' },
    { label: 'LB系列', path: '/products?category=lb' },
    { label: '纳米系列', path: '/products?category=nano' },
    { label: '芦苇系列', path: '/products?category=reed' }
  ],
  about: [
    { label: '企业简介', path: '/about' },
    { label: '发展历程', path: '/about#timeline' },
    { label: '荣誉资质', path: '/about#honors' }
  ],
  support: [
    { label: '新闻资讯', path: '/news' },
    { label: '联系我们', path: '/contact' },
    { label: '售后服务', path: '/contact#service' },
    { label: '常见问题', path: '/contact#faq' }
  ]
};

const socialLinks = [
  { name: '微信', icon: MessageCircle, qrImage: '/images/ali-qr.png?v=1', desc: '微信' },
  { name: '抖音', icon: Video, qrImage: '/images/dy-qr.png?v=1', desc: '官方抖音' }
];

export default function Footer() {
  const [hoveredQR, setHoveredQR] = useState<string | null>(null);

  return (
      <footer className="bg-deep-lake-dark text-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <img
                src="/images/logo.png"
                alt="民间艺人"
                className="h-10 w-auto brightness-100"
              />
            </div>
            <p className="text-white/70 text-sm leading-relaxed mb-6">
              浮漂行业匠心品牌，十余年来始终坚持匠心品质，为千万钓友提供专业级浮漂产品。
            </p>
            {/* 社交媒体按钮 - 悬停显示二维码 */}
            <div className="flex gap-3">
              {socialLinks.map((social) => (
                <div
                  key={social.name}
                  className="relative"
                  onMouseEnter={() => setHoveredQR(social.name)}
                  onMouseLeave={() => setHoveredQR(null)}
                >
                  <button
                    className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-all duration-300 group"
                  >
                    <social.icon className="w-4 h-4 text-warm-gold" />
                    <span className="text-sm text-white/80 group-hover:text-white">{social.name}</span>
                  </button>
                  {/* 悬停显示的二维码 */}
                  {hoveredQR === social.name && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 z-50">
                      <div className="bg-white rounded-xl p-4 shadow-2xl">
                        <div className="w-40 h-40 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                          <img
                            src={social.qrImage}
                            alt={social.desc}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                              (e.target as HTMLImageElement).parentElement!.innerHTML = `<span class="text-gray-400 text-center text-xs">${social.name}<br/>二维码</span>`;
                            }}
                          />
                        </div>
                        <p className="text-xs text-gray-500 text-center mt-2">扫码关注{social.name}</p>
                        {/* 小三角箭头 */}
                        <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-t-8 border-transparent border-t-white"></div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-lg font-serif-zh font-semibold mb-6 text-warm-gold">产品中心</h4>
            <ul className="space-y-3">
              {footerLinks.products.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-white/70 hover:text-white transition-colors duration-300 text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-serif-zh font-semibold mb-6 text-warm-gold">关于我们</h4>
            <ul className="space-y-3">
              {footerLinks.about.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-white/70 hover:text-white transition-colors duration-300 text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-serif-zh font-semibold mb-6 text-warm-gold">联系方式</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-warm-gold flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-white font-medium">0730-3761699</p>
                  <p className="text-white/60 text-xs">全国服务热线</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-warm-gold flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-white font-medium">283707357@qq.com</p>
                  <p className="text-white/60 text-xs">电子邮箱</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-warm-gold flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-white font-medium">公司地址</p>
                  <p className="text-white/60 text-xs">湖南省长沙市开福区月湖街道匍园路20号聚恒科技园1栋</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-warm-gold flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-white font-medium">周一至周六 8:30-17:30</p>
                  <p className="text-white/60 text-xs">工作时间</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-8">
         {/* 免责声明 */}
      <div className="text-center mb-6">
            <p className="text-white/50 text-xs">
              本网站仅为民间艺人浮漂品牌授权经销宣传平台，非品牌官方直营官网。
            </p>
          </div>
		  <div className="flex flex-col items-center gap-4 text-center">
            <p className="text-white/60 text-sm">
              © 2026 湖南民间艺人电子商务有限公司.ALL Rights Reserved. | <a href="http://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors duration-300">湘ICP备2026013004号-1</a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
